import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, Edit, Copy, AlertCircle, CheckCircle, Trash2, Upload, Loader2, Sparkles, BarChart3, RefreshCw, ImageIcon, Eye, Flame, Activity, Wheat, Droplet, Users, X, Download, BookOpen, FileText } from 'lucide-react';
import Navbar from '@/components/Navbar';
import RecipeEditorModal from '@/components/RecipeEditorModal';
import ContentCreator from '@/components/ContentCreator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to fix image URLs that point to old/different preview domains
const fixImageUrl = (url) => {
  if (!url) return null;
  // Skip data URLs
  if (url.startsWith('data:')) return url;
  // If URL contains any emergentagent domain OR getmealguide domain, normalize to current BACKEND_URL
  if (url.includes('emergentagent.com/api/') || url.includes('getmealguide.com/api/')) {
    const pathMatch = url.match(/\/api\/uploads\/.+$/);
    if (pathMatch) {
      return `${BACKEND_URL}${pathMatch[0]}`;
    }
  }
  return url;
};

// Recipe Template
const RECIPE_TEMPLATE = `Recipe Name:
Meal Type: [Breakfast | Main Meal | Snack]
Dietary Categories: [standard, vegan, keto, gluten_free, vegetarian, etc.]
Budget Category: [budget | moderate | premium]
Cooking Time: [5-10 min | 10-15 min | 15-25 min | 25-40 min | 40+ min]
Difficulty: [easy | medium | hard]
Spice Level: [mild | medium | spicy]
Main Protein: [Food Bank ingredient name]
Meal Prep Style: [fresh_daily | batch_2_3_days | batch_weekly | mix]

Ingredients:
[qty] [unit] [Food Bank Name]
[qty] [unit] [Food Bank Name]

Instructions:
[Full cooking instructions]

---

Recipe Name: [Next recipe]
...`;

export default function RecipeLibrary({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [recipes, setRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedMealType, setSelectedMealType] = useState('All');
  const [selectedDietCategory, setSelectedDietCategory] = useState('All');
  const [excludedAllergens, setExcludedAllergens] = useState([]); // Changed to array for multi-select exclusion
  
  // Batch Add Dialog
  const [batchAddOpen, setBatchAddOpen] = useState(false);
  const [recipeText, setRecipeText] = useState('');
  const [parsedRecipes, setParsedRecipes] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [duplicateWarnings, setDuplicateWarnings] = useState([]);
  const [foodBank, setFoodBank] = useState([]);
  
  // Delete functionality
  const [selectedRecipes, setSelectedRecipes] = useState([]);
  const [deleting, setDeleting] = useState(false);
  
  // Image upload tracking
  const [uploadingRecipeId, setUploadingRecipeId] = useState(null);
  
  // Image generation
  const [generatingImages, setGeneratingImages] = useState(false);
  const [recipesNeedingImages, setRecipesNeedingImages] = useState(0);
  
  // Broken image regeneration
  const [brokenImageCount, setBrokenImageCount] = useState(0);
  const [regenTaskId, setRegenTaskId] = useState(null);
  const [regenProgress, setRegenProgress] = useState(null);
  const [brokenImageIds, setBrokenImageIds] = useState(new Set());
  
  // Regenerate all images
  const [regeneratingAll, setRegeneratingAll] = useState(false);
  const [regenAllTaskId, setRegenAllTaskId] = useState(null);
  const [regenAllProgress, setRegenAllProgress] = useState(null);
  
  // Stats visibility
  const [showStats, setShowStats] = useState(false);
  
  // Sync from backup
  const [syncing, setSyncing] = useState(false);
  
  // Export recipes
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFilters, setExportFilters] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportMealType, setExportMealType] = useState('all');
  const [exportDietType, setExportDietType] = useState('all');
  
  // Quick diet tag editing
  const [editingDietRecipeId, setEditingDietRecipeId] = useState(null);
  
  // Client Preview
  const [clientPreviewOpen, setClientPreviewOpen] = useState(false);
  const [clientPreviewRecipe, setClientPreviewRecipe] = useState(null);
  const [clientPreviewServings, setClientPreviewServings] = useState(1);
  
  // Recipe Editor Modal
  const [editingRecipeId, setEditingRecipeId] = useState(null);
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const RECIPES_PER_PAGE = 24; // Show 24 recipes per page (4x6 grid on large screens)

  // Active tab: 'library' | 'content'
  const [activeTab, setActiveTab] = useState('library');

  // Handle recipe update from modal
  const handleRecipeUpdated = (updatedRecipe) => {
    setRecipes(prev => prev.map(r => r.id === updatedRecipe.id ? { ...r, ...updatedRecipe } : r));
  };

  // Open recipe editor modal
  const openRecipeEditor = (recipeId) => {
    setEditingRecipeId(recipeId);
    setEditorModalOpen(true);
  };

  // Navigate to different recipe in modal
  const handleRecipeNavigate = (newRecipeId) => {
    setEditingRecipeId(newRecipeId);
  };

  // Get IDs of all filtered recipes for navigation
  const filteredRecipeIds = filteredRecipes.map(r => r.id);

  // Helper to get diet type from recipe (supports both old and new schema)
  const getRecipeDietType = (recipe) => {
    // New schema: diet_type field (single value)
    if (recipe.diet_type) {
      return recipe.diet_type.toLowerCase();
    }
    // Fallback to old schema: dietary_categories array (take first)
    if (recipe.dietary_categories?.length > 0) {
      return recipe.dietary_categories[0].toLowerCase();
    }
    return 'standard';
  };
  
  // Helper to normalize meal type
  const getNormalizedMealType = (recipe) => {
    const mt = (recipe.meal_type || '').toLowerCase().replace('_', ' ');
    if (mt === 'breakfast') return 'Breakfast';
    if (['lunch', 'dinner', 'main meal', 'main_meal'].includes(mt)) return 'Main Meal';
    if (mt === 'snack') return 'Snack';
    return recipe.meal_type;
  };

  // Calculate recipe stats
  const recipeStats = {
    mealTypes: {
      'Breakfast': recipes.filter(r => getNormalizedMealType(r) === 'Breakfast').length,
      'Main Meal': recipes.filter(r => getNormalizedMealType(r) === 'Main Meal').length,
      'Snack': recipes.filter(r => getNormalizedMealType(r) === 'Snack').length,
    },
    diets: {
      'standard': recipes.filter(r => getRecipeDietType(r) === 'standard').length,
      'vegetarian': recipes.filter(r => getRecipeDietType(r) === 'vegetarian').length,
      'vegan': recipes.filter(r => getRecipeDietType(r) === 'vegan').length,
      'pescatarian': recipes.filter(r => getRecipeDietType(r) === 'pescatarian').length,
      'keto': recipes.filter(r => getRecipeDietType(r) === 'keto').length,
    },
    modifiers: {
      'high_protein': recipes.filter(r => r.high_protein === true).length,
      'halal': recipes.filter(r => r.halal === true).length,
    },
    withImages: recipes.filter(r => r.image_url && !r.image_url.startsWith('data:')).length,
    aiGenerated: recipes.filter(r => r.ai_generated).length,
  };

  const fetchRecipes = async () => {
    try {
      const response = await axios.get(`${API}/library-recipes`);
      setRecipes(response.data);
      console.log(`📚 Fetched ${response.data.length} recipes from API`);
    } catch (error) {
      toast.error('Failed to load recipes');
    } finally {
      setLoading(false);
    }
  };

  const fetchFoodBank = async () => {
    try {
      const response = await axios.get(`${API}/food-bank?limit=1000`);
      setFoodBank(response.data.foods || []);
    } catch (error) {
      console.error('Failed to load food bank');
    }
  };

  // Update diet_type (single value, not array)
  const updateDietType = async (recipeId, newDietType) => {
    try {
      await axios.patch(`${API}/library-recipes/${recipeId}/diet-tags`, {
        diet_type: newDietType
      });
      await fetchRecipes();
      toast.success(`Diet type set to ${newDietType}`);
    } catch (error) {
      console.error('Failed to update diet type:', error);
      toast.error('Failed to update diet type');
    }
  };
  
  // Toggle modifier (high_protein or halal)
  const toggleModifier = async (recipeId, modifier) => {
    const recipe = filteredRecipes.find(r => r.id === recipeId) || recipes.find(r => r.id === recipeId);
    if (!recipe) {
      toast.error('Recipe not found');
      return;
    }
    
    const currentValue = recipe[modifier] || false;
    const newValue = !currentValue;
    
    try {
      await axios.patch(`${API}/library-recipes/${recipeId}/diet-tags`, {
        [modifier]: newValue
      });
      await fetchRecipes();
      toast.success(`${modifier.replace('_', ' ')} ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error(`Failed to update ${modifier}:`, error);
      toast.error(`Failed to update ${modifier}`);
    }
  };

  const syncFromBackup = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/library-recipes/sync-from-backup`);
      if (response.data.imported > 0) {
        toast.success(`Imported ${response.data.imported} new recipes!`);
        fetchRecipes(); // Refresh the list
      } else {
        toast.info('All recipes already synced');
      }
    } catch (error) {
      console.error('Sync failed:', error);
      toast.error('Failed to sync recipes');
    } finally {
      setSyncing(false);
    }
  };

  // Fetch export filters
  const fetchExportFilters = async () => {
    try {
      const response = await axios.get(`${API}/library-recipes/export/filters`);
      setExportFilters(response.data);
    } catch (error) {
      console.error('Failed to fetch export filters:', error);
    }
  };

  // Export recipes to CSV
  const exportRecipes = async (exportSelected = false) => {
    setExporting(true);
    try {
      let url = `${API}/library-recipes/export/csv?`;
      
      if (exportSelected && selectedRecipes.length > 0) {
        // Export only selected recipes
        url += `recipe_ids=${selectedRecipes.join(',')}`;
      } else {
        // Export with filters
        if (exportMealType !== 'all') url += `meal_type=${exportMealType}&`;
        if (exportDietType !== 'all') url += `diet_type=${exportDietType}&`;
        if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}`;
      }
      
      const response = await axios.get(url, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Generate filename
      const filename = exportSelected 
        ? `recipes_selected_${selectedRecipes.length}.csv`
        : `recipes_${exportMealType}_${exportDietType}.csv`;
      link.download = filename;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`Exported recipes to ${filename}`);
      setExportDialogOpen(false);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export recipes');
    } finally {
      setExporting(false);
    }
  };

  const filterRecipes = useCallback(() => {
    let filtered = recipes;
    
    // Filter by meal type (normalize both sides)
    if (selectedMealType !== 'All') {
      filtered = filtered.filter(r => getNormalizedMealType(r) === selectedMealType);
    }
    
    // Filter by diet type (new single-value field) or modifiers
    if (selectedDietCategory !== 'All') {
      const normalizedFilter = selectedDietCategory.toLowerCase().replace(/\s+/g, '_');
      
      // Check if filtering by modifier (high_protein, halal)
      if (normalizedFilter === 'high_protein') {
        filtered = filtered.filter(r => r.high_protein === true);
      } else if (normalizedFilter === 'halal') {
        filtered = filtered.filter(r => r.halal === true);
      } else {
        // Filter by diet_type field (new schema) or fallback to dietary_categories (old schema)
        filtered = filtered.filter(r => {
          const recipeDietType = getRecipeDietType(r);
          return recipeDietType === normalizedFilter;
        });
      }
    }
    
    // Exclude recipes that contain ANY of the excluded allergens
    if (excludedAllergens.length > 0) {
      filtered = filtered.filter(r => {
        const recipeAllergens = (r.allergens || []).map(a => a.toLowerCase());
        // Recipe passes if it doesn't contain any excluded allergen
        // Use partial matching: "egg" matches "eggs", "egg", "Egg", etc.
        return !excludedAllergens.some(excluded => {
          const excludedLower = excluded.toLowerCase();
          return recipeAllergens.some(allergen => 
            allergen.includes(excludedLower) || excludedLower.includes(allergen)
          );
        });
      });
    }
    
    if (searchTerm) {
      filtered = filtered.filter(r => 
        r.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredRecipes(filtered);
  }, [recipes, selectedMealType, selectedDietCategory, excludedAllergens, searchTerm]);

  const checkRecipesNeedingImages = async () => {
    try {
      const response = await axios.get(`${API}/recipes/count-needing-images`);
      setRecipesNeedingImages(response.data.count || 0);
    } catch (error) {
      console.error('Failed to check recipes needing images:', error);
    }
  };

  const generateImagesFromPrompts = async () => {
    setGeneratingImages(true);
    try {
      const response = await axios.post(`${API}/recipes/generate-images-from-prompts?limit=50`);
      
      if (response.data.generated > 0) {
        toast.success(`Generated ${response.data.generated} images!`);
        fetchRecipes(); // Refresh to show new images
        checkRecipesNeedingImages(); // Update count
      } else if (response.data.failed > 0) {
        toast.error(`Failed to generate ${response.data.failed} images`);
      } else {
        toast.info('No recipes need images');
      }
    } catch (error) {
      console.error('Image generation failed:', error);
      // Handle validation errors (array) or string errors
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to generate images';
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      }
      toast.error(errorMessage);
    } finally {
      setGeneratingImages(false);
    }
  };

  // Check for broken images (expired runware.ai URLs)
  const checkBrokenImages = async () => {
    try {
      const response = await axios.get(`${API}/recipes/broken-image-count`);
      setBrokenImageCount(response.data.count || 0);
    } catch (error) {
      console.error('Failed to check broken images:', error);
    }
  };

  // Start bulk regeneration of broken images
  const startImageRegeneration = async () => {
    try {
      const response = await axios.post(`${API}/recipes/regenerate-broken-images`);
      if (response.data.status === 'none_needed') {
        toast.info('No broken images found');
        setBrokenImageCount(0);
        return;
      }
      const taskId = response.data.task_id;
      setRegenTaskId(taskId);
      setRegenProgress({ status: 'starting', total: response.data.total, completed: 0, failed: 0 });
      toast.success(`Started regenerating ${response.data.total} images...`);
    } catch (error) {
      toast.error('Failed to start image regeneration');
    }
  };

  // Poll regeneration progress
  useEffect(() => {
    if (!regenTaskId) return;
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/recipes/regenerate-broken-images/progress/${regenTaskId}`);
        setRegenProgress(response.data);
        if (response.data.status === 'done' || response.data.status === 'error') {
          clearInterval(interval);
          if (response.data.status === 'done') {
            toast.success(`Image regeneration complete! ${response.data.completed} images fixed.`);
            fetchRecipes();
            setBrokenImageCount(0);
            setBrokenImageIds(new Set());
          } else {
            toast.error('Image regeneration encountered an error');
          }
          setRegenTaskId(null);
        }
      } catch {
        clearInterval(interval);
        setRegenTaskId(null);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [regenTaskId]);

  // Handle image load errors - mark as broken
  const handleImageError = useCallback((recipeId) => {
    setBrokenImageIds(prev => new Set([...prev, recipeId]));
  }, []);

  useEffect(() => {
    fetchRecipes();
    fetchFoodBank();
    checkRecipesNeedingImages();
    checkBrokenImages();
  }, []);

  useEffect(() => {
    filterRecipes();
    setCurrentPage(1); // Reset to page 1 when filters change
  }, [searchTerm, selectedMealType, selectedDietCategory, excludedAllergens, recipes, filterRecipes]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredRecipes.length / RECIPES_PER_PAGE);
  const paginatedRecipes = filteredRecipes.slice(
    (currentPage - 1) * RECIPES_PER_PAGE,
    currentPage * RECIPES_PER_PAGE
  );

  const copyTemplate = () => {
    navigator.clipboard.writeText(RECIPE_TEMPLATE);
    toast.success('Template copied to clipboard!');
  };

  const detectAllergens = (ingredients) => {
    const allergens = new Set();
    
    ingredients.forEach(ing => {
      const ingName = ing.toLowerCase();
      
      // Match against Food Bank allergens
      const foodItem = foodBank.find(f => 
        f.name.toLowerCase() === ingName || ingName.includes(f.name.toLowerCase())
      );
      
      if (foodItem && foodItem.allergens) {
        foodItem.allergens.forEach(a => allergens.add(a));
      }
      
      // Common allergen patterns
      if (ingName.includes('milk') || ingName.includes('cheese') || ingName.includes('yogurt') || ingName.includes('butter') || ingName.includes('cream')) {
        allergens.add('dairy');
      }
      if (ingName.includes('egg')) allergens.add('eggs');
      if (ingName.includes('wheat') || ingName.includes('bread') || ingName.includes('pasta')) allergens.add('wheat');
      if (ingName.includes('almond') || ingName.includes('walnut') || ingName.includes('pecan') || ingName.includes('cashew')) allergens.add('tree_nuts');
      if (ingName.includes('peanut')) allergens.add('peanuts');
      if (ingName.includes('soy') || ingName.includes('tofu') || ingName.includes('tempeh')) allergens.add('soy');
      if (ingName.includes('salmon') || ingName.includes('tuna') || ingName.includes('fish') || ingName.includes('cod')) allergens.add('fish');
      if (ingName.includes('shrimp') || ingName.includes('prawn') || ingName.includes('shellfish')) allergens.add('shellfish');
    });
    
    return allergens.size > 0 ? Array.from(allergens) : ['none'];
  };

  const addGramsToIngredients = (ingredients) => {
    return ingredients.map(ing => {
      const parts = ing.trim().split(' ');
      if (parts.length < 2) return ing;
      
      // If already has grams in parentheses, skip
      if (ing.includes('(') && ing.includes('g)')) {
        return ing;
      }
      
      const qty = parts[0];
      let unit, foodName;
      
      // Check if we have 3+ parts (qty unit name) or just 2 parts (qty name)
      if (parts.length >= 3) {
        unit = parts[1].toLowerCase();
        foodName = parts.slice(2).join(' ');
      } else {
        // Only 2 parts: "2 Egg" or "1/2 Avocado"
        unit = 'piece';
        foodName = parts[1];
      }
      
      // Handle direct ml/g conversion when food is not in bank
      // For dairy/liquids, 1 ml ≈ 1 gram is a reasonable approximation
      if (unit === 'ml') {
        const qtyNum = parseFloat(qty.replace('½', '0.5').replace('¼', '0.25').replace('¾', '0.75'));
        return `${ing.trim()} (${qtyNum.toFixed(1)}g)`;
      }
      
      // Handle direct gram input
      if (unit === 'g') {
        // Already in grams, just add parentheses
        const qtyNum = parseFloat(qty.replace('½', '0.5').replace('¼', '0.25').replace('¾', '0.75'));
        return `${ing.trim()} (${qtyNum.toFixed(1)}g)`;
      }
      
      // Find food item in food bank
      const foodItem = foodBank.find(f => 
        f.name.toLowerCase() === foodName.toLowerCase() || 
        foodName.toLowerCase().includes(f.name.toLowerCase()) ||
        f.name.toLowerCase().includes(foodName.toLowerCase())
      );
      
      if (foodItem && foodItem.primary_unit_grams) {
        // Calculate grams based on primary_unit
        let grams;
        if (foodItem.primary_unit === 'g') {
          // Already in grams
          grams = parseFloat(qty);
        } else {
          // Convert using primary_unit_grams
          const qtyNum = parseFloat(qty.replace('½', '0.5').replace('¼', '0.25').replace('¾', '0.75'));
          grams = qtyNum * foodItem.primary_unit_grams;
        }
        return `${ing.trim()} (${grams.toFixed(1)}g)`;
      }
      
      return ing;
    });
  };

  const check100PercentDuplicate = (newIngredients) => {
    // Check if any existing recipe has EXACT same ingredients (100% match)
    const duplicates = [];
    
    const normalizeIngredient = (ing) => {
      return ing.toLowerCase().trim().replace(/\s+/g, ' ');
    };
    
    const newIngredientsNormalized = newIngredients.map(normalizeIngredient).sort();
    
    recipes.forEach(existingRecipe => {
      const existingIngredients = existingRecipe.ingredients.map(i => 
        normalizeIngredient(i.food_item_name || '')
      ).sort();
      
      // Check if arrays are identical
      if (newIngredientsNormalized.length === existingIngredients.length &&
          newIngredientsNormalized.every((val, idx) => val === existingIngredients[idx])) {
        duplicates.push(existingRecipe.name);
      }
    });
    
    return duplicates;
  };

  const parseRecipeText = () => {
    setParsing(true);
    setDuplicateWarnings([]);
    
    try {
      // Split by --- to get multiple recipes
      const recipeTexts = recipeText.split('---').filter(t => t.trim());
      const parsedRecipesList = [];
      const warnings = [];
      
      recipeTexts.forEach((recipeBlock, blockIndex) => {
        const lines = recipeBlock.split('\n').filter(l => l.trim());
        const recipe = {
          name: '',
          meal_type: '',
          dietary_categories: [],
          budget_category: '',
          cooking_time: '',
          difficulty: '',
          spice_level: '',
          main_protein: '',
          meal_prep_style: 'mix',
          ingredients: [],
          instructions: '',
          errors: [],
          missingFoodItems: []
        };
        
        let currentSection = '';
        let ingredientLines = [];
        let instructionLines = [];
        
        lines.forEach(line => {
          // Trim the line to remove leading/trailing whitespace
          line = line.trim();
          if (line.startsWith('Recipe Name:')) {
            recipe.name = line.replace('Recipe Name:', '').trim();
          } else if (line.startsWith('Meal Type:')) {
            recipe.meal_type = line.replace('Meal Type:', '').trim().replace(/[\[\]]/g, '');
          } else if (line.startsWith('Dietary Categories:')) {
            const cats = line.replace('Dietary Categories:', '').trim().replace(/[\[\]]/g, '');
            recipe.dietary_categories = cats.split(',').map(c => c.trim());
          } else if (line.startsWith('Budget Category:')) {
            recipe.budget_category = line.replace('Budget Category:', '').trim().replace(/[\[\]]/g, '');
          } else if (line.startsWith('Cooking Time:')) {
            recipe.cooking_time = line.replace('Cooking Time:', '').trim().replace(/[\[\]]/g, '');
          } else if (line.startsWith('Difficulty:')) {
            recipe.difficulty = line.replace('Difficulty:', '').trim().replace(/[\[\]]/g, '');
          } else if (line.startsWith('Spice Level:')) {
            recipe.spice_level = line.replace('Spice Level:', '').trim().replace(/[\[\]]/g, '');
          } else if (line.startsWith('Main Protein:')) {
            recipe.main_protein = line.replace('Main Protein:', '').trim().replace(/[\[\]]/g, '');
          } else if (line.startsWith('Meal Prep Style:')) {
            recipe.meal_prep_style = line.replace('Meal Prep Style:', '').trim().toLowerCase().replace(/[\[\]]/g, '') || 'mix';
          } else if (line.startsWith('Ingredients:')) {
            currentSection = 'ingredients';
          } else if (line.startsWith('Instructions:')) {
            currentSection = 'instructions';
          } else if (line.startsWith('Chef Notes:')) {
            // Skip Chef Notes - no longer used
            currentSection = '';
          } else if (line.startsWith('Tags:')) {
            // Skip Tags - no longer used
            currentSection = '';
          } else if (currentSection === 'ingredients' && line) {
            ingredientLines.push(line);
          } else if (currentSection === 'instructions' && line) {
            instructionLines.push(line);
          }
        });
        
        // Validate required fields
        if (!recipe.name) {
          recipe.errors.push('Missing recipe name');
        }
        if (!recipe.meal_type) {
          recipe.errors.push('Missing meal type');
        }
        if (ingredientLines.length === 0) {
          recipe.errors.push('No ingredients found');
        }
        
        // Check which ingredients are not in food bank
        ingredientLines.forEach(ingLine => {
          const parts = ingLine.split(' ', 2);
          if (parts.length >= 2) {
            // Get the full ingredient name (everything after qty and unit)
            const fullFoodName = ingLine.split(' ').slice(2).join(' ').trim();
            
            // Try to find exact match first (with or without parentheses)
            const found = foodBank.find(f => {
              const fbName = f.name.toLowerCase();
              const searchName = fullFoodName.toLowerCase();
              
              // Exact match
              if (fbName === searchName) return true;
              
              // Match without parentheses content
              const fbNameBase = fbName.split('(')[0].trim();
              const searchNameBase = searchName.split('(')[0].trim();
              if (fbNameBase === searchNameBase) return true;
              
              // Partial match (contains)
              if (fbName.includes(searchName) || searchName.includes(fbName)) return true;
              
              return false;
            });
            
            if (!found) {
              recipe.missingFoodItems.push(fullFoodName);
            }
          }
        });
        
        // Add grams to ingredients
        recipe.ingredients = addGramsToIngredients(ingredientLines);
        
        // Auto-detect allergens
        recipe.allergens = detectAllergens(ingredientLines);
        
        // Check for 100% duplicates
        const duplicates = check100PercentDuplicate(ingredientLines);
        if (duplicates.length > 0) {
          warnings.push({
            recipeName: recipe.name,
            duplicateOf: duplicates
          });
          recipe.errors.push(`Duplicate of: ${duplicates.join(', ')}`);
        }
        
        recipe.instructions = instructionLines.join('\n');
        parsedRecipesList.push(recipe);
      });
      
      setDuplicateWarnings(warnings);
      setParsedRecipes(parsedRecipesList);
      
      const errorCount = parsedRecipesList.filter(r => r.errors.length > 0).length;
      const missingCount = parsedRecipesList.filter(r => r.missingFoodItems.length > 0).length;
      
      if (errorCount > 0) {
        toast.error(`⚠️ ${errorCount} recipe(s) have errors. Review before saving.`);
      } else if (missingCount > 0) {
        toast.warning(`⚠️ ${missingCount} recipe(s) have missing food items. Review before saving.`);
      } else {
        toast.success(`✅ Parsed ${parsedRecipesList.length} recipe(s) successfully!`);
      }
    } catch (error) {
      toast.error('Failed to parse recipes. Check format.');
      console.error(error);
    } finally {
      setParsing(false);
    }
  };

  const saveParsedRecipes = async () => {
    if (duplicateWarnings.length > 0) {
      toast.error('Cannot save duplicate recipes. Please modify ingredients.');
      return;
    }
    
    setSaving(true);
    try {
      for (const recipe of parsedRecipes) {
        await axios.post(`${API}/library-recipes`, recipe);
      }
      toast.success(`Saved ${parsedRecipes.length} recipe(s)!`);
      setBatchAddOpen(false);
      setRecipeText('');
      setParsedRecipes([]);
      fetchRecipes();
    } catch (error) {
      toast.error('Failed to save recipes');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedRecipes.length === paginatedRecipes.length) {
      setSelectedRecipes([]);
    } else {
      setSelectedRecipes(paginatedRecipes.map(r => r.id));
    }
  };

  const handleSelectAllRecipes = () => {
    // Select ALL recipes (not just current page)
    if (selectedRecipes.length === filteredRecipes.length) {
      setSelectedRecipes([]);
    } else {
      setSelectedRecipes(filteredRecipes.map(r => r.id));
    }
  };

  const handleSelectRecipe = (recipeId) => {
    if (selectedRecipes.includes(recipeId)) {
      setSelectedRecipes(selectedRecipes.filter(id => id !== recipeId));
    } else {
      setSelectedRecipes([...selectedRecipes, recipeId]);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRecipes.length === 0) {
      toast.error('No recipes selected');
      return;
    }

    if (!window.confirm(`Delete ${selectedRecipes.length} recipe(s)? This cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      for (const recipeId of selectedRecipes) {
        await axios.delete(`${API}/library-recipes/${recipeId}`);
      }
      toast.success(`Deleted ${selectedRecipes.length} recipe(s)!`);
      setSelectedRecipes([]);
      fetchRecipes();
    } catch (error) {
      toast.error('Failed to delete recipes');
    } finally {
      setDeleting(false);
    }
  };

  const handleQuickImageUpload = async (recipeId, file) => {
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingRecipeId(recipeId);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/library-recipes/${recipeId}/upload-image`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      // Update the recipe in the list
      setRecipes(recipes.map(r => 
        r.id === recipeId ? { ...r, image_url: response.data.image_url } : r
      ));
      
      toast.success('Image uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingRecipeId(null);
    }
  };

  const handleTestImageRegen = async (recipeId) => {
    setUploadingRecipeId(recipeId);
    const recipe = recipes.find(r => r.id === recipeId);
    
    toast.info(`Regenerating image for "${recipe?.name}" with improved quality...`);
    
    try {
      const response = await axios.post(
        `${API}/recipes/test-image-generation`,
        { recipe_id: recipeId }
      );

      if (response.data.success) {
        // Update the recipe in the list with new image
        setRecipes(recipes.map(r => 
          r.id === recipeId ? { ...r, image_url: response.data.new_image_url } : r
        ));
        
        // Remove from broken images if it was there
        setBrokenImageIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(recipeId);
          return newSet;
        });
        
        toast.success(`✨ New professional-quality image generated for "${recipe?.name}"!`);
      } else {
        toast.error(response.data.message || 'Failed to regenerate image');
      }
    } catch (error) {
      console.error('Image regeneration error:', error);
      
      // Properly extract error message
      let errorMessage = 'Failed to regenerate image';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } else {
          errorMessage = JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setUploadingRecipeId(null);
    }
  };

  const handleRegenerateAllImages = async () => {
    if (!confirm('Regenerate ALL 220 recipe images with improved quality? This will replace existing images and take 30-60 minutes.')) {
      return;
    }

    setRegeneratingAll(true);
    toast.info('Starting bulk image regeneration in the background...');

    try {
      const response = await axios.post(
        `${API}/recipes/regenerate-all-images`,
        { limit: 500, force: true }
      );

      if (response.data.task_id) {
        setRegenAllTaskId(response.data.task_id);
        setRegenAllProgress(response.data);
        toast.success(`Started regenerating ${response.data.total} images. This will run in the background.`);
      } else if (response.data.status === 'already_running') {
        setRegenAllTaskId(response.data.task_id);
        setRegenAllProgress(response.data);
        toast.info('Image regeneration already in progress');
      } else {
        toast.info(response.data.message || 'No images to regenerate');
        setRegeneratingAll(false);
      }
    } catch (error) {
      console.error('Bulk regeneration error:', error);
      
      let errorMessage = 'Failed to start regeneration';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(e => e.msg || JSON.stringify(e)).join(', ');
        } else {
          errorMessage = JSON.stringify(detail);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      setRegeneratingAll(false);
    }
  };

  // Poll for regeneration progress
  useEffect(() => {
    if (!regenAllTaskId) return;

    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(
          `${API}/recipes/regenerate-all-images/progress/${regenAllTaskId}`
        );

        setRegenAllProgress(response.data);

        if (response.data.status === 'complete') {
          clearInterval(pollInterval);
          setRegeneratingAll(false);
          setRegenAllTaskId(null);
          toast.success(
            `✨ Successfully regenerated ${response.data.completed}/${response.data.total} images! ${response.data.failed} failed.`
          );
          
          // Refresh recipes
          await fetchRecipes();
        } else if (response.data.status === 'error') {
          clearInterval(pollInterval);
          setRegeneratingAll(false);
          setRegenAllTaskId(null);
          toast.error(`Regeneration failed: ${response.data.error}`);
        }
      } catch (error) {
        console.error('Error polling progress:', error);
      }
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(pollInterval);
  }, [regenAllTaskId]);


  const mealTypes = ['All', 'Breakfast', 'Main Meal', 'Snack'];
  // Diet categories - simplified to match new schema
  // Primary diet types: standard, vegetarian, vegan, pescatarian, keto
  // Modifiers: high_protein, halal (boolean flags)
  const dietCategories = [
    { value: 'All', label: 'All Diets' },
    { value: 'standard', label: 'Standard' },
    { value: 'vegetarian', label: 'Vegetarian' },
    { value: 'vegan', label: 'Vegan' },
    { value: 'pescatarian', label: 'Pescatarian' },
    { value: 'keto', label: 'Keto' },
    { value: 'high_protein', label: 'High Protein' },
    { value: 'halal', label: 'Halal' },
  ];
  // Allergens for exclusion
  const allergenOptions = ['Gluten', 'Dairy', 'Eggs', 'Nuts', 'Peanuts', 'Soy', 'Fish', 'Shellfish', 'Sesame'];

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="page-container px-3 md:px-6">
        <div className="mb-6 md:mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-2">Recipe Library</h1>
            <p className="text-sm md:text-lg text-gray-600">{recipes.length} recipes in library</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {(brokenImageCount > 0 || regenTaskId) && (
              <Button
                onClick={startImageRegeneration}
                disabled={!!regenTaskId}
                variant="outline"
                size="lg"
                className="border-orange-500 text-orange-600 hover:bg-orange-50"
                data-testid="fix-broken-images-btn"
              >
                {regenTaskId ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <ImageIcon className="h-5 w-5 mr-2" />
                )}
                {regenTaskId 
                  ? `Fixing... ${regenProgress?.completed || 0}/${regenProgress?.total || brokenImageCount}` 
                  : `Fix Broken Images (${brokenImageCount})`
                }
              </Button>
            )}
            {recipesNeedingImages > 0 && (
              <Button
                onClick={generateImagesFromPrompts}
                disabled={generatingImages}
                variant="outline"
                size="lg"
                className="border-purple-500 text-purple-600 hover:bg-purple-50"
              >
                {generatingImages ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <ImageIcon className="h-5 w-5 mr-2" />
                )}
                {generatingImages ? 'Generating...' : `Generate Images (${recipesNeedingImages})`}
              </Button>
            )}
            <Button
              onClick={handleRegenerateAllImages}
              disabled={regeneratingAll}
              variant="outline"
              size="lg"
              className="border-purple-600 text-purple-700 hover:bg-purple-50"
            >
              {regeneratingAll ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  {regenAllProgress 
                    ? `Regenerating... ${regenAllProgress.completed || 0}/${regenAllProgress.total || 0}` 
                    : 'Starting...'}
                </>
              ) : (
                <>
                  <Sparkles className="h-5 w-5 mr-2" />
                  Regenerate All Images
                </>
              )}
            </Button>
            {recipes.length < 600 && (
              <Button
                onClick={syncFromBackup}
                disabled={syncing}
                variant="outline"
                size="lg"
                className="border-green-500 text-green-600 hover:bg-green-50"
              >
                {syncing ? (
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-5 w-5 mr-2" />
                )}
                {syncing ? 'Syncing...' : 'Sync Recipes'}
              </Button>
            )}
            <Button
              onClick={() => {
                fetchExportFilters();
                setExportDialogOpen(true);
              }}
              variant="outline"
              size="lg"
              className="border-blue-500 text-blue-600 hover:bg-blue-50"
            >
              <Download className="h-5 w-5 mr-2" />
              Export Recipes
            </Button>
            <Button
              onClick={() => navigate('/coach/csv-import')}
              className="bg-[#1a61e8] hover:bg-[#1a61e8]/90"
              size="lg"
            >
              <Upload className="h-5 w-5 mr-2" />
              Import Recipes
            </Button>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            onClick={() => setActiveTab('library')}
            data-testid="tab-library"
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'library'
                ? 'border-[#1a61e8] text-[#1a61e8]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <BookOpen className="h-4 w-4" />
            Recipe Library
          </button>
          <button
            onClick={() => setActiveTab('content')}
            data-testid="tab-content"
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'content'
                ? 'border-[#1a61e8] text-[#1a61e8]'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <FileText className="h-4 w-4" />
            Content Creator
          </button>
        </div>

        {activeTab === 'content' ? (
          <ContentCreator recipes={recipes} />
        ) : (
          <>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm font-medium text-gray-600">Filters</span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowStats(!showStats)}
                className="text-purple-600 hover:text-purple-700"
              >
                <BarChart3 className="h-4 w-4 mr-1" />
                {showStats ? 'Hide Stats' : 'Show Stats'}
              </Button>
            </div>
            
            {/* Stats Panel */}
            {showStats && (
              <div className="mb-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100">
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-purple-600" />
                  Recipe Breakdown
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Meal Types */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">By Meal Type</h4>
                    <div className="space-y-1">
                      {Object.entries(recipeStats.mealTypes).map(([type, count]) => (
                        <div key={type} className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">{type}</span>
                          <Badge variant={count < 50 ? "destructive" : count < 100 ? "outline" : "default"} className="text-xs">
                            {count}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Diet Types */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">By Diet Type</h4>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {Object.entries(recipeStats.diets)
                        .sort((a, b) => b[1] - a[1])
                        .map(([diet, count]) => (
                        <div key={diet} className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">{diet.replace('_', ' ')}</span>
                          <Badge variant={count < 20 ? "destructive" : count < 50 ? "outline" : "default"} className="text-xs">
                            {count}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Other Stats */}
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Other Stats</h4>
                    <div className="space-y-1">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Total Recipes</span>
                        <Badge className="text-xs bg-blue-600">{recipes.length}</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">With Images</span>
                        <Badge variant={recipeStats.withImages < recipes.length * 0.5 ? "destructive" : "default"} className="text-xs">
                          {recipeStats.withImages} ({Math.round(recipeStats.withImages / recipes.length * 100)}%)
                        </Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">AI Generated</span>
                        <Badge variant="outline" className="text-xs">{recipeStats.aiGenerated}</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Manual</span>
                        <Badge variant="outline" className="text-xs">{recipes.length - recipeStats.aiGenerated}</Badge>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Low count warnings */}
                <div className="mt-3 pt-3 border-t border-purple-100">
                  <p className="text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span> Low (&lt;20)
                    </span>
                    <span className="inline-flex items-center gap-1 ml-3">
                      <span className="w-2 h-2 rounded-full bg-gray-400"></span> Medium
                    </span>
                    <span className="inline-flex items-center gap-1 ml-3">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span> Good (50+)
                    </span>
                  </p>
                </div>
              </div>
            )}
            
            {/* NEW FEATURE BANNER */}
            <div className="mb-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <Sparkles className="h-5 w-5 text-purple-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">
                    ✨ Improved Image Quality Available
                  </h3>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    Your recipe images can now be regenerated with professional photorealistic quality using enhanced Runware settings (steps: 40, better prompts). 
                    Hover over any recipe image and click the purple <Sparkles className="h-3 w-3 inline-block mx-0.5" /> button to regenerate individually, or use the "Regenerate All Images" button above.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <Input
                  placeholder="Search recipes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full"
                />
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Meal Type</label>
                <select
                  value={selectedMealType}
                  onChange={(e) => setSelectedMealType(e.target.value)}
                  className="w-full p-2 border rounded-md text-sm"
                >
                  {mealTypes.map(type => (
                    <option key={type} value={type}>{type === 'All' ? 'All Meal Types' : type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Diet Type</label>
                <select
                  value={selectedDietCategory}
                  onChange={(e) => setSelectedDietCategory(e.target.value)}
                  className="w-full p-2 border rounded-md text-sm"
                >
                  {dietCategories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Allergen Exclusion - Clickable Badges */}
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                <span className="text-sm font-medium text-gray-700">Exclude Allergens</span>
                {excludedAllergens.length > 0 && (
                  <button 
                    onClick={() => setExcludedAllergens([])}
                    className="text-xs text-gray-500 hover:text-gray-700 ml-2"
                  >
                    (Clear all)
                  </button>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-2">Click to exclude recipes containing these allergens</p>
              <div className="flex flex-wrap gap-2">
                {allergenOptions.map(allergen => {
                  const isExcluded = excludedAllergens.includes(allergen);
                  return (
                    <Badge 
                      key={allergen}
                      variant={isExcluded ? "default" : "outline"}
                      className={`cursor-pointer transition-all text-xs ${
                        isExcluded 
                          ? 'bg-red-500 hover:bg-red-600 text-white border-red-500' 
                          : 'hover:bg-red-50 hover:border-red-300 text-gray-600'
                      }`}
                      onClick={() => {
                        if (isExcluded) {
                          setExcludedAllergens(excludedAllergens.filter(a => a !== allergen));
                        } else {
                          setExcludedAllergens([...excludedAllergens, allergen]);
                        }
                      }}
                    >
                      {isExcluded && <span className="mr-1">✕</span>}
                      {allergen}
                    </Badge>
                  );
                })}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions */}
        {filteredRecipes.length > 0 && (
          <div className="mb-4 flex items-center gap-3 bg-white p-3 rounded-lg border flex-wrap">
            <input
              type="checkbox"
              checked={selectedRecipes.length === paginatedRecipes.length && paginatedRecipes.length > 0}
              onChange={handleSelectAll}
              className="h-4 w-4 cursor-pointer"
            />
            <span className="text-sm text-gray-700">
              {selectedRecipes.length === paginatedRecipes.length && paginatedRecipes.length > 0 ? 'Deselect All' : 'Select All'} (page)
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleSelectAllRecipes}
            >
              {selectedRecipes.length === filteredRecipes.length ? 'Deselect All' : `Select All ${filteredRecipes.length} Recipes`}
            </Button>
            {selectedRecipes.length > 0 && (
              <>
                <span className="text-sm text-gray-500">({selectedRecipes.length} selected)</span>
                <Button
                  onClick={handleDeleteSelected}
                  disabled={deleting}
                  variant="destructive"
                  size="sm"
                  className="ml-auto"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  {deleting ? 'Deleting...' : `Delete Selected (${selectedRecipes.length})`}
                </Button>
              </>
            )}
          </div>
        )}

        {/* Pagination Info */}
        {filteredRecipes.length > RECIPES_PER_PAGE && (
          <div className="mb-4 text-sm text-gray-600 bg-white p-3 rounded-lg border flex justify-between items-center">
            <span>
              Showing {((currentPage - 1) * RECIPES_PER_PAGE) + 1}-{Math.min(currentPage * RECIPES_PER_PAGE, filteredRecipes.length)} of {filteredRecipes.length} recipes
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                Previous
              </Button>
              <span className="px-3">Page {currentPage} of {totalPages}</span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        )}

        {/* Recipe Grid */}
        {loading ? (
          <div className="text-center py-12">Loading recipes...</div>
        ) : filteredRecipes.length === 0 ? (
          <div className="text-center py-12 text-gray-500">No recipes found</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedRecipes.map(recipe => (
              <Card 
                key={`${recipe.id}-${(recipe.dietary_categories || []).join(',')}`}
                className={`hover:shadow-lg transition-shadow overflow-hidden relative group ${selectedRecipes.includes(recipe.id) ? 'ring-2 ring-blue-500' : ''}`}
              >
                {/* Hover Tooltip with Ingredients */}
                <div className="absolute top-0 left-full ml-2 z-50 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                  <div className="bg-white border border-gray-300 rounded-lg shadow-xl p-4 w-72 max-h-96 overflow-y-auto">
                    <h4 className="font-bold text-sm mb-2 border-b pb-2">Ingredients:</h4>
                    <div className="space-y-1">
                      {recipe.ingredients && recipe.ingredients.length > 0 ? (
                        recipe.ingredients.map((ing, idx) => (
                          <div key={idx} className="text-xs flex justify-between items-start gap-2">
                            <span className="flex-1">{ing.food_bank_name || ing.food_item_name || ing.display_text}</span>
                            <span className="text-gray-500 whitespace-nowrap">
                              {ing.display_text || `${Math.round(ing.grams)}g`}
                            </span>
                          </div>
                        ))
                      ) : (
                        <p className="text-xs text-gray-400 italic">No ingredients listed</p>
                      )}
                    </div>
                    
                    {/* Cooking Essentials */}
                    {recipe.cooking_essentials && recipe.cooking_essentials.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-dashed">
                        <h5 className="font-semibold text-xs text-gray-600 mb-1">Cooking Essentials:</h5>
                        <div className="space-y-1">
                          {recipe.cooking_essentials.map((ess, idx) => (
                            <div key={idx} className="text-xs flex justify-between items-start gap-2 text-gray-500">
                              <span className="flex-1">{ess.food_bank_name || ess.food_item_name || ess.name}</span>
                              <span className="whitespace-nowrap">{Math.round(ess.grams)}g</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* Recipe Image with Upload Option */}
                <div className="relative">
                  {recipe.image_url && !brokenImageIds.has(recipe.id) ? (
                    <div className="cursor-pointer relative group h-48 bg-gray-100 flex items-center justify-center" onClick={() => openRecipeEditor(recipe.id)}>
                      <img 
                        src={fixImageUrl(recipe.image_url)} 
                        alt={recipe.name}
                        className="w-full h-full object-contain"
                        onError={() => handleImageError(recipe.id)}
                      />
                      {/* Upload & Regenerate overlay on hover */}
                      <div className="absolute inset-0 bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                        <label 
                          htmlFor={`upload-${recipe.id}`}
                          className="cursor-pointer bg-white rounded-full p-2 hover:bg-gray-100 flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                          title="Upload custom image"
                        >
                          {uploadingRecipeId === recipe.id ? (
                            <Loader2 className="h-6 w-6 text-gray-700 animate-spin" />
                          ) : (
                            <Upload className="h-6 w-6 text-gray-700" />
                          )}
                        </label>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTestImageRegen(recipe.id);
                          }}
                          className="cursor-pointer bg-purple-600 text-white rounded-full p-2 hover:bg-purple-700 transition-colors flex items-center gap-1"
                          disabled={uploadingRecipeId === recipe.id}
                          title="Regenerate with improved AI quality"
                        >
                          {uploadingRecipeId === recipe.id ? (
                            <Loader2 className="h-6 w-6 animate-spin" />
                          ) : (
                            <Sparkles className="h-6 w-6" />
                          )}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-100 h-48 flex items-center justify-center relative">
                      <label 
                        htmlFor={`upload-${recipe.id}`}
                        className="cursor-pointer flex flex-col items-center gap-2 hover:bg-gray-200 w-full h-full justify-center transition-colors"
                      >
                        {uploadingRecipeId === recipe.id ? (
                          <Loader2 className="h-8 w-8 text-gray-400 animate-spin" />
                        ) : (
                          <>
                            <Upload className="h-8 w-8 text-gray-400" />
                            <span className="text-sm text-gray-500">Upload Image</span>
                          </>
                        )}
                      </label>
                    </div>
                  )}
                  
                  {/* Hidden file input */}
                  <input
                    id={`upload-${recipe.id}`}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    disabled={uploadingRecipeId === recipe.id}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        handleQuickImageUpload(recipe.id, file);
                      }
                      // Reset input so same file can be selected again
                      e.target.value = '';
                    }}
                  />
                </div>
                
                <CardHeader>
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={selectedRecipes.includes(recipe.id)}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleSelectRecipe(recipe.id);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      className="h-4 w-4 mt-1 cursor-pointer"
                    />
                    <div className="flex-1 cursor-pointer" onClick={() => openRecipeEditor(recipe.id)}>
                      <CardTitle className="text-lg">{recipe.name}</CardTitle>
                      <div className="flex gap-2 flex-wrap mt-2">
                        <Badge variant="outline">{recipe.meal_type}</Badge>
                        {recipe.cooking_time && (
                          <Badge variant="secondary">⏱️ {recipe.cooking_time}</Badge>
                        )}
                        {recipe.difficulty && (
                          <Badge variant="secondary">{recipe.difficulty}</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="cursor-pointer" onClick={() => openRecipeEditor(recipe.id)}>
                  <div className="space-y-2">
                    {/* Macros */}
                    <div className="grid grid-cols-4 gap-2 text-sm">
                      <div className="text-center">
                        <div className="font-semibold text-gray-900">{recipe.total_kcal?.toFixed(0)}</div>
                        <div className="text-xs text-gray-500">kcal</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-blue-600">{recipe.total_protein?.toFixed(0)}g</div>
                        <div className="text-xs text-gray-500">protein</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-orange-600">{recipe.total_carbs?.toFixed(0)}g</div>
                        <div className="text-xs text-gray-500">carbs</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-green-600">{recipe.total_fat?.toFixed(0)}g</div>
                        <div className="text-xs text-gray-500">fat</div>
                      </div>
                    </div>
                    
                    {/* Diet Type & Modifiers - Click to edit */}
                    <div 
                      className="flex gap-1 flex-wrap cursor-pointer hover:bg-gray-50 p-1 -m-1 rounded transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingDietRecipeId(editingDietRecipeId === recipe.id ? null : recipe.id);
                      }}
                    >
                      {/* Show diet_type badge */}
                      <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                        {(recipe.diet_type || getRecipeDietType(recipe)).replace('_', ' ')}
                      </span>
                      {/* Show modifier badges */}
                      {recipe.high_protein && (
                        <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">high protein</span>
                      )}
                      {recipe.halal && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">halal</span>
                      )}
                      <span className="text-xs text-gray-400 ml-1">✏️</span>
                    </div>
                    
                    {/* Quick Diet Type Editor - NEW SCHEMA */}
                    {editingDietRecipeId === recipe.id && (
                      <div 
                        className="mt-2 p-2 bg-blue-50 rounded-lg border border-blue-200"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* Diet Type Selection (single select) */}
                        <div className="text-xs font-medium text-blue-800 mb-2">Diet Type (select one):</div>
                        <div className="flex flex-wrap gap-1 mb-3">
                          {['standard', 'vegetarian', 'vegan', 'pescatarian', 'keto'].map(dietType => {
                            const isActive = getRecipeDietType(recipe) === dietType;
                            return (
                              <button
                                key={dietType}
                                onClick={() => updateDietType(recipe.id, dietType)}
                                className={`text-xs px-2 py-1 rounded transition-colors ${
                                  isActive 
                                    ? 'bg-blue-600 text-white' 
                                    : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-100'
                                }`}
                              >
                                {dietType.replace('_', ' ')}
                              </button>
                            );
                          })}
                        </div>
                        
                        {/* Modifiers (toggles) */}
                        <div className="text-xs font-medium text-blue-800 mb-2">Modifiers (toggle):</div>
                        <div className="flex flex-wrap gap-1">
                          <button
                            onClick={() => toggleModifier(recipe.id, 'high_protein')}
                            className={`text-xs px-2 py-1 rounded transition-colors ${
                              recipe.high_protein 
                                ? 'bg-purple-600 text-white' 
                                : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            high protein
                          </button>
                          <button
                            onClick={() => toggleModifier(recipe.id, 'halal')}
                            className={`text-xs px-2 py-1 rounded transition-colors ${
                              recipe.halal 
                                ? 'bg-green-600 text-white' 
                                : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-100'
                            }`}
                          >
                            halal
                          </button>
                        </div>
                        <button
                          onClick={() => setEditingDietRecipeId(null)}
                          className="mt-2 text-xs text-blue-600 hover:underline"
                        >
                          Done
                        </button>
                      </div>
                    )}
                    
                    {/* Allergens - Compact Display */}
                    {recipe.allergens && recipe.allergens.length > 0 && recipe.allergens[0] !== 'none' && (
                      <div className="flex items-center gap-1.5 text-xs text-orange-700">
                        <span className="font-medium">⚠</span>
                        <span className="truncate">
                          {(() => {
                            // Normalize and deduplicate
                            let allergens = [...new Set(recipe.allergens.map(a => a.toLowerCase()))];
                            
                            // Consolidate redundant allergens
                            if (allergens.includes('nuts') && allergens.includes('tree_nuts')) {
                              allergens = allergens.filter(a => a !== 'tree_nuts');
                            } else if (allergens.includes('tree_nuts')) {
                              allergens = allergens.map(a => a === 'tree_nuts' ? 'nuts' : a);
                            }
                            
                            // Format and display
                            return allergens
                              .map(a => a.replace('_', ' ').charAt(0).toUpperCase() + a.replace('_', ' ').slice(1))
                              .join(', ');
                          })()}
                        </span>
                      </div>
                    )}
                    
                    {/* Client Preview Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full mt-2 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        setClientPreviewRecipe(recipe);
                        setClientPreviewServings(1);
                        setClientPreviewOpen(true);
                      }}
                    >
                      <Eye className="h-3 w-3 mr-1" />
                      Client Preview
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Bottom Pagination */}
        {filteredRecipes.length > RECIPES_PER_PAGE && (
          <div className="mt-6 flex justify-center items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
            >
              First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span className="px-4 py-2 text-sm">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
            >
              Last
            </Button>
          </div>
        )}

        </>
        )}

        {/* Batch Add Dialog */}
        <Dialog open={batchAddOpen} onOpenChange={setBatchAddOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Batch Add Recipes</DialogTitle>
              <DialogDescription>
                Paste up to 20 recipes at a time. Separate each recipe with &ldquo;---&rdquo;. Do not include Chef Notes or Tags. Auto-detects allergens, validates food items, and checks for duplicates.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Template */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold">Template</h3>
                  <Button onClick={copyTemplate} variant="outline" size="sm">
                    <Copy className="h-4 w-4 mr-2" />
                    Copy Template
                  </Button>
                </div>
                <div className="bg-gray-50 p-3 rounded-md text-xs font-mono whitespace-pre-wrap max-h-40 overflow-y-auto">
                  {RECIPE_TEMPLATE}
                </div>
              </div>

              {/* Paste Area */}
              <div>
                <h3 className="font-semibold mb-2">Paste Recipe</h3>
                <Textarea
                  value={recipeText}
                  onChange={(e) => setRecipeText(e.target.value)}
                  placeholder="Paste your recipe text here..."
                  className="min-h-[200px] font-mono text-sm"
                />
              </div>

              {/* Parse Button */}
              <Button
                onClick={parseRecipeText}
                disabled={!recipeText || parsing}
                className="w-full"
              >
                {parsing ? 'Parsing...' : 'Parse Recipe'}
              </Button>

              {/* Duplicate Warning */}
              {duplicateWarnings.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-red-900">100% Duplicate Detected!</h4>
                      {duplicateWarnings.map((warn, idx) => (
                        <p key={idx} className="text-sm text-red-800 mt-1">
                          &ldquo;{warn.recipeName}&rdquo; has identical ingredients to: {warn.duplicateOf.join(', ')}
                        </p>
                      ))}
                      <p className="text-sm text-red-800 mt-2">
                        ⚠️ Cannot save duplicate. Please modify ingredients.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Parsed Preview */}
              {parsedRecipes.length > 0 && (
                <div className="space-y-3">
                  <div className={`${duplicateWarnings.length === 0 ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'} border rounded-lg p-4`}>
                    <div className="flex items-start gap-2 mb-3">
                      {duplicateWarnings.length === 0 ? (
                        <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                      )}
                      <div>
                        <h4 className={`font-semibold ${duplicateWarnings.length === 0 ? 'text-green-900' : 'text-yellow-900'}`}>
                          Parsed {parsedRecipes.length} Recipe(s)
                        </h4>
                        <p className={`text-sm ${duplicateWarnings.length === 0 ? 'text-green-800' : 'text-yellow-800'}`}>
                          {duplicateWarnings.length > 0 ? 'Review errors before saving' : 'Review and save to library'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto space-y-3">
                    {parsedRecipes.map((recipe, idx) => (
                      <div key={idx} className={`rounded p-3 space-y-2 border ${
                        recipe.errors.length > 0 ? 'bg-red-50 border-red-200' : 
                        recipe.missingFoodItems.length > 0 ? 'bg-yellow-50 border-yellow-200' : 
                        'bg-white border-gray-200'
                      }`}>
                        <div className="flex items-start justify-between">
                          <h3 className="font-bold">{recipe.name || `Recipe ${idx + 1}`}</h3>
                          {recipe.errors.length === 0 && recipe.missingFoodItems.length === 0 && (
                            <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">✓ Valid</Badge>
                          )}
                          {recipe.errors.length > 0 && (
                            <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300">✗ Error</Badge>
                          )}
                          {recipe.errors.length === 0 && recipe.missingFoodItems.length > 0 && (
                            <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">⚠ Warning</Badge>
                          )}
                        </div>

                        {/* Errors */}
                        {recipe.errors.length > 0 && (
                          <div className="bg-red-100 border border-red-300 rounded p-2">
                            <p className="text-xs font-semibold text-red-900 mb-1">Errors:</p>
                            <ul className="text-xs text-red-800 space-y-1">
                              {recipe.errors.map((err, i) => (
                                <li key={i}>• {err}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Missing Food Items */}
                        {recipe.missingFoodItems.length > 0 && (
                          <div className="bg-yellow-100 border border-yellow-300 rounded p-2">
                            <p className="text-xs font-semibold text-yellow-900 mb-1">Missing from Food Bank:</p>
                            <ul className="text-xs text-yellow-800 space-y-1">
                              {recipe.missingFoodItems.map((item, i) => (
                                <li key={i}>• {item}</li>
                              ))}
                            </ul>
                            <p className="text-xs text-yellow-800 mt-1 italic">Add these to Food Bank first or recipe will have 0 calories</p>
                          </div>
                        )}

                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <p><span className="font-semibold">Meal Type:</span> {recipe.meal_type || 'N/A'}</p>
                          <p><span className="font-semibold">Cooking Time:</span> {recipe.cooking_time || 'N/A'}</p>
                          <p><span className="font-semibold">Difficulty:</span> {recipe.difficulty || 'N/A'}</p>
                          <p><span className="font-semibold">Main Protein:</span> {recipe.main_protein || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="font-semibold text-sm">Auto-Detected Allergens:</p>
                          <div className="flex gap-1 flex-wrap mt-1">
                            {recipe.allergens?.map(a => (
                              <Badge key={a} variant="outline" className="text-xs">{a}</Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <p className="font-semibold text-sm">Ingredients ({recipe.ingredients?.length || 0}):</p>
                          <ul className="text-xs space-y-1 mt-1">
                            {recipe.ingredients?.slice(0, 5).map((ing, i) => (
                              <li key={i}>• {ing.food_bank_name || ing.food_item_name || ing}</li>
                            ))}
                            {recipe.ingredients?.length > 5 && (
                              <li className="text-gray-500">... and {recipe.ingredients.length - 5} more</li>
                            )}
                          </ul>
                          {recipe.cooking_essentials?.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-dashed">
                              <p className="font-semibold text-xs text-gray-600">Cooking Essentials:</p>
                              <ul className="text-xs space-y-1 mt-1 text-gray-500">
                                {recipe.cooking_essentials.map((ess, i) => (
                                  <li key={i}>• {ess.food_bank_name || ess.food_item_name || ess.name} ({Math.round(ess.grams)}g)</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={saveParsedRecipes}
                    disabled={saving || duplicateWarnings.length > 0 || parsedRecipes.some(r => r.errors.length > 0)}
                    className="w-full mt-4 bg-green-600 hover:bg-green-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 
                     duplicateWarnings.length > 0 || parsedRecipes.some(r => r.errors.length > 0) ? 
                     'Fix errors before saving' : 
                     `Save ${parsedRecipes.filter(r => r.errors.length === 0).length} Valid Recipe(s) to Library`}
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Client Preview Dialog */}
        <Dialog open={clientPreviewOpen} onOpenChange={setClientPreviewOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <div className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-blue-600" />
                <DialogTitle>Client View Preview</DialogTitle>
              </div>
              <DialogDescription>
                This is how your client will see this recipe
              </DialogDescription>
            </DialogHeader>
            
            {clientPreviewRecipe && (
              <div className="space-y-4 mt-4 bg-gray-50 rounded-lg p-4 border">
                {/* Recipe Image */}
                <div className="w-full h-48 bg-gradient-to-br from-orange-50 to-amber-100 rounded-lg overflow-hidden flex items-center justify-center">
                  {clientPreviewRecipe.image_url ? (
                    <img 
                      src={fixImageUrl(clientPreviewRecipe.image_url)} 
                      alt={clientPreviewRecipe.name}
                      className="w-full h-full object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <Flame className="h-12 w-12 text-orange-300" />
                  )}
                </div>

                {/* Recipe Name */}
                <h3 className="text-lg font-semibold text-gray-900">{clientPreviewRecipe.name}</h3>

                {/* Macros - 2x2 grid */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border">
                    <Flame className="h-5 w-5 text-orange-500 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">{Math.round((clientPreviewRecipe.total_kcal || 0) * clientPreviewServings)}</p>
                      <p className="text-xs text-gray-500">kcal</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border">
                    <Activity className="h-5 w-5 text-red-500 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">{Math.round((clientPreviewRecipe.total_protein || 0) * clientPreviewServings)}g</p>
                      <p className="text-xs text-gray-500">protein</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border">
                    <Wheat className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">{Math.round((clientPreviewRecipe.total_carbs || 0) * clientPreviewServings)}g</p>
                      <p className="text-xs text-gray-500">carbs</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border">
                    <Droplet className="h-5 w-5 text-blue-500 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-gray-900">{Math.round((clientPreviewRecipe.total_fat || 0) * clientPreviewServings)}g</p>
                      <p className="text-xs text-gray-500">fat</p>
                    </div>
                  </div>
                </div>

                {/* Servings Selector */}
                <div className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-600" />
                    <span className="text-sm text-gray-700">Servings</span>
                  </div>
                  <Select value={clientPreviewServings.toString()} onValueChange={(val) => setClientPreviewServings(parseInt(val))}>
                    <SelectTrigger className="w-20 h-8 text-sm">
                      <SelectValue placeholder="1" />
                    </SelectTrigger>
                    <SelectContent>
                      {[1,2,3,4,5,6].map(n => (
                        <SelectItem key={n} value={n.toString()}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Ingredients */}
                <div>
                  <h4 className="font-semibold text-sm text-gray-900 mb-2">Ingredients</h4>
                  <div className="bg-white rounded-lg p-3 border">
                    <ul className="space-y-1.5">
                      {clientPreviewRecipe.ingredients?.map((ing, idx) => {
                        const scaledGrams = ing.grams ? Math.round(ing.grams * clientPreviewServings) : null;
                        const displayName = ing.food_bank_name || ing.food_item_name || ing.name;
                        const quantity = ing.quantity ? (ing.quantity * clientPreviewServings).toFixed(1).replace(/\.0$/, '') : null;
                        const unit = ing.unit || 'g';
                        
                        // Hide "Piece" unit from display - just show quantity + name
                        const showUnit = unit.toLowerCase() !== 'piece' && unit.toLowerCase() !== 'g';
                        
                        return (
                          <li key={idx} className="flex items-start justify-between text-sm text-gray-700">
                            <span className="flex items-start flex-1">
                              <span className="text-blue-600 mr-2">•</span>
                              <span>
                                {quantity && unit.toLowerCase() !== 'g' ? `${quantity}${showUnit ? ` ${unit}` : ''} ` : ''}{displayName}
                              </span>
                            </span>
                            {scaledGrams && (
                              <span className="text-gray-400 ml-2">({scaledGrams}g)</span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>

                {/* Cooking Essentials */}
                {clientPreviewRecipe.cooking_essentials?.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-900 mb-2">Cooking Essentials</h4>
                    <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                      <ul className="space-y-1.5">
                        {clientPreviewRecipe.cooking_essentials.map((ess, idx) => {
                          const displayName = ess.food_bank_name || ess.food_item_name || ess.name;
                          const quantity = ess.quantity ? ess.quantity.toFixed(1).replace(/\.0$/, '') : null;
                          const unit = ess.unit || 'g';
                          
                          // Hide "Piece" unit from display
                          const showUnit = unit.toLowerCase() !== 'piece' && unit.toLowerCase() !== 'g';
                          
                          return (
                            <li key={idx} className="flex items-start justify-between text-sm text-gray-700">
                              <span className="flex items-start flex-1">
                                <span className="text-amber-600 mr-2">•</span>
                                <span>
                                  {quantity && unit.toLowerCase() !== 'g' ? `${quantity}${showUnit ? ` ${unit}` : ''} ` : ''}{displayName}
                                </span>
                              </span>
                              {ess.grams && (
                                <span className="text-gray-400 ml-2">({Math.round(ess.grams)}g)</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </div>
                )}

                {/* Instructions */}
                {clientPreviewRecipe.instructions && (
                  <div>
                    <h4 className="font-semibold text-sm text-gray-900 mb-2">Instructions</h4>
                    <div className="bg-white rounded-lg border overflow-hidden">
                      {clientPreviewRecipe.instructions.split('\n').filter(step => step.trim()).map((step, idx) => {
                        // Remove leading number and period if present (e.g., "1. Step" -> "Step")
                        const cleanStep = step.replace(/^\d+\.\s*/, '').trim();
                        if (!cleanStep) return null;
                        
                        return (
                          <div 
                            key={idx} 
                            className={`flex items-start gap-3 p-3 ${idx > 0 ? 'border-t' : ''}`}
                          >
                            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold">
                              {idx + 1}
                            </div>
                            <p className="text-sm text-gray-700 pt-0.5">{cleanStep}</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Mock Log Button */}
                <div className="pt-2">
                  <Button className="w-full bg-blue-600 hover:bg-blue-700" disabled>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Log This Meal (Client Action)
                  </Button>
                  <p className="text-xs text-gray-400 text-center mt-1">Preview only - button disabled</p>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Recipe Editor Modal */}
        <RecipeEditorModal 
          recipeId={editingRecipeId}
          open={editorModalOpen}
          onClose={() => {
            setEditorModalOpen(false);
            setEditingRecipeId(null);
          }}
          onRecipeUpdated={handleRecipeUpdated}
          recipeIds={filteredRecipeIds}
          onNavigate={handleRecipeNavigate}
        />

        {/* Export Recipes Dialog */}
        <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Export Recipes to CSV</DialogTitle>
              <DialogDescription>
                Export your recipes in the same format used for importing.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Export selected option */}
              {selectedRecipes.length > 0 && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800 mb-2">
                    <strong>{selectedRecipes.length}</strong> recipes selected
                  </p>
                  <Button
                    onClick={() => exportRecipes(true)}
                    disabled={exporting}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {exporting ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Export Selected Only
                  </Button>
                </div>
              )}
              
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-3">Or export by filters:</p>
                
                {/* Meal Type Filter */}
                <div className="space-y-2 mb-4">
                  <label className="text-sm text-gray-600">Meal Type</label>
                  <Select value={exportMealType} onValueChange={setExportMealType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Meal Types</SelectItem>
                      {exportFilters?.meal_types?.map(mt => (
                        <SelectItem key={mt} value={mt}>{mt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Diet Type Filter */}
                <div className="space-y-2 mb-4">
                  <label className="text-sm text-gray-600">Diet Type</label>
                  <Select value={exportDietType} onValueChange={setExportDietType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Diet Types</SelectItem>
                      {exportFilters?.diet_types?.map(dt => (
                        <SelectItem key={dt} value={dt}>{dt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Total count */}
                <p className="text-sm text-gray-500 mb-4">
                  Total recipes in library: <strong>{exportFilters?.total_recipes || recipes.length}</strong>
                </p>
                
                <Button
                  onClick={() => exportRecipes(false)}
                  disabled={exporting}
                  className="w-full"
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  Export {exportMealType === 'all' && exportDietType === 'all' ? 'All' : 'Filtered'} Recipes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
